import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Logarithmic function parameters: a * log_b(x-h) + k */
export interface LogarithmicParams {
  /** Vertical scale */
  a: number;
  /** Base of logarithm (must be positive and not 1) */
  b: number;
  /** Horizontal shift (vertical asymptote at x = h) */
  h: number;
  /** Vertical shift */
  k: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for logarithmic functions.
 * Handle is placed at (h + 1, k) since log_b(1) = 0.
 * This means the handle is always on the curve at y = k.
 */
export const LOGARITHMIC_META: FunctionMeta<LogarithmicParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'tangent',
    'secant',
    'derivative',
    'asymptotes',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'domain',
    'range',
  ],
  handleConfigs: {
    translate: {
      xParam: 'h',
      yParam: 'k',
      // Handle at (h + 1, k) since log_b(1) = 0
      toPosition: (p) => [p['h'] + 1, p['k']],
      // When dragged to (x, y): h = x - 1, k = y
      fromPosition: (x, y) => ({ h: x - 1, k: y }),
    },
    'translate-x': {
      xParam: 'h',
      yParam: 'k',
      toPosition: (p) => [p['h'] + 1, p['k']],
      fromPosition: (x, _y, p) => ({ h: x - 1, k: p['k'] }),
    },
    'translate-y': {
      xParam: 'h',
      yParam: 'k',
      toPosition: (p) => [p['h'] + 1, p['k']],
      fromPosition: (_x, y, p) => ({ h: p['h'], k: y }),
    },
  },
  defaultParams: {
    a: 1,
    b: Math.E,
    h: 0,
    k: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [0.1, 10],
    h: [-10, 10],
    k: [-10, 10],
  },
};
